package com.sddamico.components.sections

import androidx.compose.runtime.*
import com.sddamico.SitePalette
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.sddamico.HeadingFontFamily
import com.sddamico.components.widgets.IconButton
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.sddamico.components.motion.PressTransition
import com.sddamico.components.motion.PressableStyle
import com.sddamico.components.motion.SlidingUnderline
import com.sddamico.components.motion.rememberPresence
import com.sddamico.components.motion.rememberReducedMotion
import com.sddamico.components.motion.stableBoldLabel
import com.sddamico.components.motion.underlineTarget
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener

val NavHeaderStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
}

@Composable
private fun Logo() {
    Link("/", variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)) {
        Img(
            src = "/logo.png",
            alt = "Fantasy Country Club",
            attrs = Modifier
                .height(6.cssRem)
                .displayIfAtLeast(Breakpoint.MD)
                .toAttrs()
        )
        Img(
            src = "/logo.png",
            alt = "FCC",
            attrs = Modifier
                .height(3.5.cssRem)
                .displayUntil(Breakpoint.MD)
                .toAttrs()
        )
    }
}

@Composable
private fun NavLink(path: String, text: String) {
    val ctx = rememberPageContext()
    val isActive = ctx.route.path == path
    Link(
        path,
        text,
        Modifier
            .color(if (isActive) SitePalette.primary else SitePalette.textSecondary)
            .fontSize(0.875.cssRem)
            .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Medium)
            .transition(Transition.of("color", Durations.HOVER.ms, Ease.Out.fn))
            .stableBoldLabel(text)
            .underlineTarget(isActive),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
    )
}

@Composable
private fun TeamBadge(clubName: String) {
    Div(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(9999.px)
            .padding(topBottom = 0.25.cssRem, leftRight = 0.75.cssRem)
            .fontSize(0.75.cssRem)
            .color(SitePalette.textSecondary)
            .toAttrs()
    ) {
        Text(clubName)
    }
}

@Composable
private fun SignInButton() {
    val ctx = rememberPageContext()
    Div(
        PressableStyle.toModifier()
            .backgroundColor(SitePalette.primary)
            .color(Colors.White)
            .borderRadius(8.px)
            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
            .fontSize(0.875.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .transition(Transition.of("background-color", Durations.HOVER.ms, Ease.Out.fn), PressTransition)
            .onClick { ctx.router.tryRoutingTo("/sign-in") }
            .toAttrs()
    ) {
        Text("Sign In")
    }
}

@Composable
private fun AuthenticatedMenuItems() {
    val authState = LocalAuthState.current
    val effectiveRole = authState.user?.realUserRole ?: authState.user?.role
    NavLink("/", "League Home")
    NavLink("/clubhouse", "My Clubhouse")
    NavLink("/schedule", "Schedule")
    NavLink("/standings", "Standings")
    NavLink("/results", "Results")
    NavLink("/players", "Players")
    NavLink("/transactions", "Transactions")
    if (effectiveRole == "LEAGUE_MANAGER" || effectiveRole == "LEAGUE_ADMIN") {
        LmToolsDropdown()
    }
}

@Composable
private fun LmToolsDropdown() {
    val ctx = rememberPageContext()
    val currentPath = ctx.route.path
    val isActive = currentPath == "/manage-rosters" || currentPath == "/manage-schedule" || currentPath == "/manage-clubs"
    var showDropdown by remember { mutableStateOf(false) }
    val dropdown = rememberPresence(showDropdown, exitMillis = Durations.EXIT)
    val reducedMotion = rememberReducedMotion()

    // Close on route change
    LaunchedEffect(currentPath) {
        showDropdown = false
    }

    // Close on Escape
    DisposableEffect(showDropdown) {
        if (!showDropdown) return@DisposableEffect onDispose {}
        val listener = EventListener { e ->
            if (e.asDynamic().key == "Escape") showDropdown = false
        }
        document.addEventListener("keydown", listener)
        onDispose { document.removeEventListener("keydown", listener) }
    }

    Div(
        Modifier
            .position(Position.Relative)
            .toAttrs()
    ) {
        // Trigger
        Row(
            Modifier
                .color(if (isActive) SitePalette.primary else SitePalette.textSecondary)
                .fontSize(0.875.cssRem)
                .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Medium)
                .cursor(Cursor.Pointer)
                .gap(0.25.cssRem)
                .transition(Transition.of("color", Durations.HOVER.ms, Ease.Out.fn))
                .onClick { showDropdown = !showDropdown },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText("LM Tools", Modifier.stableBoldLabel("LM Tools").underlineTarget(isActive))
            SpanText(
                "\u25BE",
                Modifier
                    .rotate(if (showDropdown) 180.deg else 0.deg)
                    .transition(Transition.of("transform", Durations.POPOVER.ms, Ease.Out.fn))
            )
        }

        // Backdrop to close on outside click
        if (showDropdown) {
            Div(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px)
                    .left(0.px)
                    .width(100.vw)
                    .height(100.vh)
                    .styleModifier { property("z-index", "99") }
                    .onClick { showDropdown = false }
                    .toAttrs()
            )
        }

        // Dropdown panel, scaling out of the trigger's corner
        if (dropdown.isMounted) {
            val shown = dropdown.isShown
            val duration = if (shown) Durations.POPOVER else Durations.EXIT
            Column(
                Modifier
                    .position(Position.Absolute)
                    .top(100.percent)
                    .margin(top = 0.5.cssRem)
                    .backgroundColor(SitePalette.surface)
                    .border(1.px, LineStyle.Solid, SitePalette.border)
                    .borderRadius(8.px)
                    .padding(0.5.cssRem)
                    .gap(0.25.cssRem)
                    .opacity(if (shown) 1 else 0)
                    .pointerEvents(if (shown) PointerEvents.Auto else PointerEvents.None)
                    .styleModifier {
                        property("right", "0")
                        property("z-index", "100")
                        property("box-shadow", "0 4px 12px rgba(0,0,0,0.3)")
                        property("transform-origin", "top right")
                        if (!reducedMotion) {
                            property("transform", if (shown) "none" else "translateY(-4px) scale(0.96)")
                        }
                        property(
                            "transition",
                            "opacity ${duration}ms ${Ease.Out.css}, transform ${duration}ms ${Ease.Out.css}"
                        )
                    }
            ) {
                DropdownNavLink("/manage-rosters", "Manage Rosters")
                DropdownNavLink("/manage-schedule", "Manage Schedule")
                DropdownNavLink("/manage-clubs", "Manage Clubs")
            }
        }
    }
}

@Composable
private fun DropdownNavLink(path: String, text: String) {
    val ctx = rememberPageContext()
    val isActive = ctx.route.path == path
    Link(
        path,
        text,
        Modifier
            .color(if (isActive) SitePalette.primary else SitePalette.textSecondary)
            .fontSize(0.8125.cssRem)
            .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Medium)
            .padding(topBottom = 0.375.cssRem, leftRight = 0.75.cssRem)
            .borderRadius(6.px)
            .transition(Transition.of("color", 150.ms))
            .styleModifier { property("white-space", "nowrap") },
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant)
    )
}

@Composable
private fun HamburgerButton(onClick: () -> Unit) {
    IconButton(onClick) {
        HamburgerIcon()
    }
}

@Composable
private fun CloseButton(onClick: () -> Unit) {
    IconButton(onClick) {
        CloseIcon()
    }
}

@Composable
fun NavHeader() {
    val authState = LocalAuthState.current
    val isAuthenticated = authState.user != null
    val ctx = rememberPageContext()
    val currentPath = ctx.route.path

    Row(NavHeaderStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
        Logo()

        Spacer()

        // Desktop nav
        SlidingUnderline(
            activeKey = currentPath,
            color = SitePalette.primary,
            modifier = Modifier.displayIfAtLeast(Breakpoint.MD),
        ) {
            Row(Modifier.gap(1.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                if (isAuthenticated) {
                    // Links arrive with the auth check, once per visit; fading them in keeps the header from flashing.
                    Row(
                        Modifier
                            .gap(1.5.cssRem)
                            .animation(FadeInKeyframes.toAnimation(Durations.ENTER.ms, Ease.Out.fn)),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AuthenticatedMenuItems()
                        authState.user?.clubName?.let { TeamBadge(it) }
                        NavLink("/settings", "Settings")
                    }
                } else if (!authState.loading) {
                    SignInButton()
                }
            }
        }

        // Mobile nav
        Row(
            Modifier
                .fontSize(1.5.cssRem)
                .gap(1.cssRem)
                .displayUntil(Breakpoint.MD),
            verticalAlignment = Alignment.CenterVertically
        ) {
            var menuOpen by remember { mutableStateOf(false) }

            // Dismiss menu on route change (link clicks in the drawer)
            LaunchedEffect(currentPath) {
                menuOpen = false
            }

            HamburgerButton(onClick = { menuOpen = true })

            SideMenu(
                open = menuOpen,
                isAuthenticated = isAuthenticated,
                clubName = authState.user?.clubName,
                close = { menuOpen = false },
            )
        }
    }
}

@Composable
private fun SideMenu(
    open: Boolean,
    isAuthenticated: Boolean,
    clubName: String?,
    close: () -> Unit,
) {
    val presence = rememberPresence(open, exitMillis = Durations.MOVE)
    val reducedMotion = rememberReducedMotion()
    if (!presence.isMounted) return
    val shown = presence.isShown

    DisposableEffect(Unit) {
        val listener = EventListener { e ->
            if (e.asDynamic().key == "Escape") close()
        }
        document.addEventListener("keydown", listener)
        onDispose { document.removeEventListener("keydown", listener) }
    }

    val duration = if (shown) Durations.DRAWER else Durations.MOVE

    // Scrim
    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .styleModifier { property("background-color", "rgba(0, 0, 0, 0.5)"); property("z-index", "1000") }
            .opacity(if (shown) 1 else 0)
            .transition(Transition.of("opacity", duration.ms, Ease.Out.fn))
            .onClick { close() }
            .toAttrs()
    )

    Column(
        Modifier
            .position(Position.Fixed)
            .top(0.px).right(0.px).bottom(0.px)
            .width(14.cssRem)
            .padding(top = 1.cssRem, leftRight = 1.cssRem)
            .gap(1.5.cssRem)
            .backgroundColor(SitePalette.surface)
            .borderLeft(1.px, LineStyle.Solid, SitePalette.border)
            .fontSize(1.5.cssRem)
            .styleModifier {
                property("z-index", "1001")
                if (reducedMotion) {
                    property("opacity", if (shown) "1" else "0")
                    property("transition", "opacity ${duration}ms ${Ease.Out.css}")
                } else {
                    property("transform", if (shown) "none" else "translateX(100%)")
                    property("transition", "transform ${duration}ms ${Ease.Drawer.css}")
                }
            },
        horizontalAlignment = Alignment.End
    ) {
        CloseButton(onClick = { close() })
        Column(
            Modifier.padding(right = 0.75.cssRem).gap(1.5.cssRem).fontSize(1.1.cssRem)
                .onClick { close() },
            horizontalAlignment = Alignment.End
        ) {
            var index = 0
            @Composable
            fun Item(content: @Composable () -> Unit) {
                DrawerItem(index++, shown, reducedMotion, content)
            }
            if (isAuthenticated) {
                val mobileEffectiveRole = LocalAuthState.current.user?.realUserRole ?: LocalAuthState.current.user?.role
                Item { NavLink("/", "League Home") }
                Item { NavLink("/clubhouse", "My Clubhouse") }
                Item { NavLink("/schedule", "Schedule") }
                Item { NavLink("/standings", "Standings") }
                Item { NavLink("/results", "Results") }
                Item { NavLink("/players", "Players") }
                Item { NavLink("/transactions", "Transactions") }
                if (mobileEffectiveRole == "LEAGUE_MANAGER" || mobileEffectiveRole == "LEAGUE_ADMIN") {
                    Item { SpanText("LM Tools", Modifier.color(SitePalette.textMuted).fontSize(0.75.cssRem)) }
                    Item { NavLink("/manage-rosters", "Manage Rosters") }
                    Item { NavLink("/manage-schedule", "Manage Schedule") }
                    Item { NavLink("/manage-clubs", "Manage Clubs") }
                }
                clubName?.let {
                    Item { SpanText(it, Modifier.color(SitePalette.textMuted).fontSize(0.75.cssRem)) }
                }
                Item { NavLink("/settings", "Settings") }
            } else {
                Item { NavLink("/sign-in", "Sign In") }
            }
        }
    }
}

/** Drawer links follow the panel in on a short stagger; on close they leave together with it. */
@Composable
private fun DrawerItem(index: Int, shown: Boolean, reducedMotion: Boolean, content: @Composable () -> Unit) {
    val delay = if (shown) 60 + index * 25 else 0
    val duration = if (shown) Durations.ENTER else Durations.EXIT
    Div(
        Modifier
            .opacity(if (shown) 1 else 0)
            .styleModifier {
                if (!reducedMotion) property("transform", if (shown) "none" else "translateX(16px)")
                property(
                    "transition",
                    "opacity ${duration}ms ${Ease.Out.css} ${delay}ms, transform ${duration}ms ${Ease.Out.css} ${delay}ms"
                )
            }
            .toAttrs()
    ) {
        content()
    }
}
